import { getSessionVariable } from '@services/storage';

import { env } from '@helpers/env';

import fs from 'fs';
import path from 'path';

let featuresMemoryConfig: string;

declare global {
    interface Window {
        mcommerce?: Record<string, boolean>;
        omnikit?: {
            insertBeforeId: string;
        };
    }
}

export function generateConfig() {
    return {
        domain: env('NEXT_PUBLIC_DOMAIN') ?? '',
        version: process.env.APP_VERSION ?? '',
        siteURl: env('NEXT_PUBLIC_SITE_URL') ?? '',
        enableAnalytics: process.env.ENABLE_ANALYTICS ?? env('NEXT_PUBLIC_ENABLE_ANALYTICS') ?? '',
        bench: env('NEXT_PUBLIC_BENCH') ?? '',
        api: {
            url: process.env.API_URL ?? env('NEXT_PUBLIC_API_URL') ?? '',
        },
        bytelApi: {
            url: process.env.BYTEL_API ?? env('NEXT_PUBLIC_BYTEL_API') ?? '',
        },
        sapi: {
            url: process.env.SAPI_URL ?? env('NEXT_PUBLIC_SAPI_URL') ?? '',
        },
        sapic: {
            url: process.env.SAPIC_URL ?? env('NEXT_PUBLIC_SAPIC_URL') ?? '',
        },
        widget: {
            faiEligibilityWidget: env('NEXT_PUBLIC_FAI_ELIGIBILITY_WIDGET') ?? '',
        },
        proxy: {
            url: process.env.PROXY_URL,
        },
        skeepers: {
            url: process.env.SKEEPERS_URL,
            id: process.env.SKEEPERS_ID,
        },
        prisme: {
            url: env('NEXT_PUBLIC_PRISME_URL') ?? '',
            active: process.env.LOG_PRISME_ACTIVE
                ? process.env.LOG_PRISME_ACTIVE === 'true'
                : env('NEXT_PUBLIC_LOG_PRISME_ACTIVE') === 'true',
            authorizeUrls: [
                env('NEXT_PUBLIC_DOMAIN'),
                env('NEXT_PUBLIC_SAPIC_URL'),
                env('NEXT_PUBLIC_SAPI_URL'),
                env('NEXT_PUBLIC_BYTEL_API'),
                env('NEXT_PUBLIC_FAQ_API_URL'),
                '/webapi/',
            ].filter((url) => url !== undefined),
        },
        oauth2: {
            clientId: env('NEXT_PUBLIC_OAUTH2_CLIENT_ID') ?? '',
            scope: env('NEXT_PUBLIC_OAUTH2_SCOPE') ?? '',
            url: env('NEXT_PUBLIC_OAUTH2_URL') ?? '',
            authorizationUrl: env('NEXT_PUBLIC_OAUTH2_AUTHORIZATION_URL') ?? '',
            tokenUrl: process.env.OAUTH2_TOKEN_URL,
            userInfoUrl: process.env.OAUTH2_USERINFO_URL,
            tgtUrl: env('NEXT_PUBLIC_OAUTH2_TGT_URL') ?? '',
            tgtCallbackUrl: env('NEXT_PUBLIC_OAUTH2_TGT_CALLBACK') ?? '',
        },
        assets: {
            images: {
                url: env('NEXT_PUBLIC_ASSETS_IMAGES') ?? '',
            },
            cms: {
                url: process.env.ASSETS_CMS ?? env('NEXT_PUBLIC_ASSETS_CMS') ?? '',
            },
            partials: {
                url: process.env.PARTIALS_URL ?? env('NEXT_PUBLIC_PARTIALS_URL') ?? '',
                header: {
                    default: env('NEXT_PUBLIC_HEADER_PARTIAL_URL') ?? '',
                    light: env('NEXT_PUBLIC_HEADER_PARTIAL_URL_LIGHT') ?? '',
                },
            },
            sliders: {
                script: env('NEXT_PUBLIC_ASSETS_SLIDERS_SCRIPT') ?? '',
            },
            palpatine: {
                product: process.env.PALPATINE_PRODUCT_API,
                mcommerce: process.env.MCOMMERCE_API,
                checkout: process.env.CHECKOUT_API,
            },
        },
        tracking: {
            env: env('NEXT_PUBLIC_TRACKING_ENV') as 'PROD' | 'TEST',
            enabled: env('NEXT_PUBLIC_TRACKING_ENABLED') === 'true',
            host: env('NEXT_PUBLIC_TRACKING_HOST') ?? '',
            debug: env('NEXT_PUBLIC_TRACKING_DEBUG') === 'true',
            universe: env('NEXT_PUBLIC_TRACKING_UNIVERSE') ?? '',
        },
        logger: {
            http: {
                logCached: process.env.LOG_HTTP_CACHED === 'true',
            },
        },
        cache: {
            ttl: {
                products: env('NEXT_PUBLIC_CACHE_TTL_PRODUCTS') ?? '',
                cms: env('NEXT_PUBLIC_CACHE_TTL_CMS') ?? '',
                user: env('NEXT_PUBLIC_CACHE_TTL_USER') ?? '',
                config: env('NEXT_PUBLIC_CACHE_TTL_CONFIG') ?? '',
            },
        },
        googleMaps: {
            apiKey: env('NEXT_PUBLIC_GOOGLE_MAPS_API_KEY') ?? '',
        },
        faqApi: {
            url: process.env.FAQ_API_URL ?? env('NEXT_PUBLIC_FAQ_API_URL') ?? '',
        },
        mcommerceApi: {
            url: process.env.MCOMMERCE_API ?? env('NEXT_PUBLIC_MCOMMERCE_API') ?? '',
        },
        rocApi: {
            url: process.env.ROC_URL_CE ?? env('NEXT_PUBLIC_ROC_URL_CE') ?? '',
        },
        siteRootUrl: {
            url: process.env.SITE_URL ?? env('NEXT_PUBLIC_SITE_URL') ?? '',
        },
        bAndYou: {
            url: process.env.BANDYOU_URL ?? env('NEXT_PUBLIC_BANDYOU_URL') ?? '',
        },
        features: {
            rfeEnabled: env('NEXT_PUBLIC_RFE_ENABLED') === 'true',
        },
        offers: {
            pureFibreGencodeMax: process.env.GENCODE_PURE_FIBRE ?? env('NEXT_PUBLIC_GENCODE_PURE_FIBRE_MAX') ?? '',
            pureFibreGencodeStandard:
                process.env.GENCODE_PURE_FIBRE_STANDARD ?? env('NEXT_PUBLIC_GENCODE_PURE_FIBRE_STANDARD') ?? '',
            bboxPlusTvGencode: process.env.GENCODE_BBOX_PLUS_TV ?? env('NEXT_PUBLIC_GENCODE_BBOX_PLUS_TV') ?? '',
            boostPhoneGencodes: env('NEXT_PUBLIC_BOOST_PHONE_GENCODES'),
        },
    };
}

export const getFeaturesConfigContent = (): string => {
    if (featuresMemoryConfig === undefined) {
        featuresMemoryConfig = fs.readFileSync(path.resolve('./public', 'features.json'), 'utf8');
    }

    return featuresMemoryConfig;
};

export const getFeaturesConfig = (): Record<string, boolean> => {
    // Overwrite feature flags via session for dynamic changes.
    // This allows for the alteration of feature flags at runtime based on session variables.
    // Useful for testing or conditional feature deployment.
    // See feature module in https://gitlab.int.nbyt.fr/A360/the-monkeys-paw.
    if (isServer()) {
        return JSON.parse(getFeaturesConfigContent());
    } else {
        const sessionFeatures = JSON.parse(getSessionVariable('mcommerce_features') || '{}');
        const windowFeatures = window.mcommerce!;

        return { ...windowFeatures, ...sessionFeatures };
    }
};

export const isServer = () => {
    return typeof window === 'undefined';
};

export const MEDIA_BASE_URL = 'https://bouyguestelecom.fr/assets/media/full/image/CMS/ecommerce/byou';
